import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { LookupManager } from "../components/LookupManager";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Wrench, PlusCircle, Pencil, ClipboardList, Search, CheckCircle2, AlertTriangle, XCircle, Zap, MonitorSmartphone, HeartPulse, Lock, Printer, ShieldAlert, Trash2, FlaskConical, Barcode, FileText, Upload, X, RefreshCw, Tag, ArrowLeftRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HazmatSection } from "../components/HazmatSection";
import { DeviceBarcodeLabel } from "../components/DeviceBarcodeLabel";
import { PPMStickerLabel } from "../components/PPMStickerLabel";
import { BulkBarcodePrint } from "../components/BulkBarcodePrint";
import { BulkPPMPrint } from "../components/BulkPPMPrint";

const STATUS_META = {
  operational:        { label: "يعمل بكفاءة",  icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
  needs_maintenance:  { label: "يحتاج صيانة",  icon: AlertTriangle, cls: "bg-amber-100 text-amber-800" },
  out_of_service:     { label: "خارج الخدمة",  icon: XCircle, cls: "bg-red-100 text-red-800" },
};

const ELECTRICAL_TYPE_LABELS = {
  ac: "AC / تكييف",
  computer: "Computer / كمبيوتر",
  monitor: "Monitor / شاشة",
  phone: "Phone / هاتف",
  printer: "Printer / طابعة",
  camera: "Camera / كاميرا",
  switch: "Switch / سويتش شبكة",
  router: "Router / راوتر",
  th_sensor: "T&H Sensor / حساس حرارة ورطوبة",
  other: "Other / أخرى",
};

const ELECTRIC_TYPE_LABELS = {
  ac: "AC / مكيف",
  electrical_panel: "Electrical Panel / لوحة كهرباء",
  ups: "UPS / مزود طاقة احتياطي",
  other: "Other / أخرى",
};

const SAFETY_TYPE_LABELS = {
  fire_extinguisher: "Fire Extinguisher / طفاية حريق",
  fire_hose: "Fire Hose / خرطوم حريق",
  smoke_detector: "Smoke Detector / كاشف دخان",
  fire_alarm: "Fire Alarm / إنذار حريق",
  emergency_light: "Emergency Light / إضاءة طوارئ",
  exit_sign: "Exit Sign / لافتة خروج",
  first_aid_kit: "First Aid Kit / حقيبة إسعافات",
  aed: "AED / مزيل رجفان",
  sprinkler: "Sprinkler / رشاش مياه",
  co2_detector: "CO2 Detector / كاشف CO2",
  apron: "Apron / مريلة واقية من الأشعة",
  other: "Other / أخرى",
};

const BiomedDevices = () => {
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const canManage = user?.role === "admin" || user?.is_biomed_officer === true;
  const isAdmin = user?.role === "admin";
  const toList = (obj) => Object.entries(obj).map(([value, label]) => ({ value, label }));
  const [electricalTypes, setElectricalTypes] = useState(toList(ELECTRICAL_TYPE_LABELS));
  const [electricTypes, setElectricTypes] = useState(toList(ELECTRIC_TYPE_LABELS));
  const [safetyTypes, setSafetyTypes] = useState(toList(SAFETY_TYPE_LABELS));
  const typeLabel = (list, fallbackMap, v) => (list.find(x => x.value === v)?.label || fallbackMap[v] || v || "—");

  const loadTypeLookups = () => {
    axios.get(`${API}/lookups/electrical_types`, { headers }).then(r => { if (r.data?.length) setElectricalTypes(r.data); }).catch(() => {});
    axios.get(`${API}/lookups/electric_types`, { headers }).then(r => { if (r.data?.length) setElectricTypes(r.data); }).catch(() => {});
    axios.get(`${API}/lookups/safety_types`, { headers }).then(r => { if (r.data?.length) setSafetyTypes(r.data); }).catch(() => {});
  };
  useEffect(() => { loadTypeLookups(); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["biomed", "electrical", "electric", "safety", "hazmat"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "biomed";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab -> URL param so sidebar links reflect selection
  useEffect(() => {
    const current = searchParams.get("tab");
    if (activeTab !== current) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // React to URL param changes (when sidebar link is clicked while on this page)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["biomed", "electrical", "electric", "safety", "hazmat"].includes(t) && t !== activeTab) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [devices, setDevices] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [offices, setOffices] = useState([]);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("all"); // "all" | `${type}:${id}`

  const emptyForm = {
    biomed_no: "", device_name: "", manufacturer: "", model_no: "", serial_no: "",
    device_type: "biomed", electrical_type: "", electric_type: "", safety_type: "", device_floor: "", quantity: 1,
    location_type: "clinic", location_id: "", location_text: "", notes: ""
  };
  const [formDialog, setFormDialog] = useState(null); // { mode, device? }
  const [form, setForm] = useState(emptyForm);

  const emptyEval = { status: "operational", condition_score: 5, notes: "", evaluation_date: new Date().toISOString().slice(0, 10) };
  const [evalDialog, setEvalDialog] = useState(null); // device
  const [evalForm, setEvalForm] = useState(emptyEval);

  const [detail, setDetail] = useState(null);
  const [transferDialog, setTransferDialog] = useState(null);
  const [transferForm, setTransferForm] = useState({ target: "", notes: "" });

  const submitTransfer = async () => {
    if (!transferForm.target) { toast.error("اختر الموقع الجديد"); return; }
    const [lt, lid] = transferForm.target.split(":");
    try {
      await axios.post(`${API}/biomed-devices/${transferDialog.id}/transfer`, { location_type: lt, location_id: lid, notes: transferForm.notes }, { headers });
      toast.success("تم نقل الجهاز بنجاح");
      setTransferDialog(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل النقل"); }
  };

  const openDetail = async (d) => {
    setDetail(d);
    try {
      const res = await axios.get(`${API}/biomed-devices/${d.id}`, { headers });
      setDetail(res.data);
    } catch (e) { /* keep list row data */ }
  };
  const [hazmatAddSignal, setHazmatAddSignal] = useState(0);
  const [barcodeDevice, setBarcodeDevice] = useState(null);
  const [ppmDevice, setPpmDevice] = useState(null);
  const [bulkPrint, setBulkPrint] = useState(false);
  const [bulkPPM, setBulkPPM] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [existingAttach, setExistingAttach] = useState(null);
  const [saveBusy, setSaveBusy] = useState(false);

  const emptyReplaceForm = { rtype: "replacement", reason: "", date: new Date().toISOString().slice(0, 10), old_serial: "", new_serial: "", new_model: "", new_manufacturer: "" };
  const [replaceDialog, setReplaceDialog] = useState(null); // device
  const [replaceForm, setReplaceForm] = useState(emptyReplaceForm);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [repPrintItem, setRepPrintItem] = useState(null);

  const submitReplacement = async () => {
    if (!replaceForm.reason.trim()) { toast.error("السبب مطلوب"); return; }
    if (!replaceForm.date) { toast.error("التاريخ مطلوب"); return; }
    if (replaceForm.rtype === "replacement" && !replaceForm.new_serial.trim()) { toast.error("الرقم التسلسلي الجديد مطلوب عند الإحلال"); return; }
    setReplaceBusy(true);
    try {
      const res = await axios.post(`${API}/biomed-devices/${replaceDialog.id}/replacements`, replaceForm, { headers });
      toast.success(replaceForm.rtype === "replacement" ? "تم توثيق إحلال الجهاز" : "تم توثيق تجديد الجهاز");
      setReplaceDialog(res.data);
      setReplaceForm({ ...emptyReplaceForm, old_serial: res.data.serial_no || "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    } finally {
      setReplaceBusy(false);
    }
  };

  const onAttachSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("يجب أن يكون الملف بصيغة PDF"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الملف يجب أن يكون أقل من 10MB"); return; }
    setAttachFile(file);
  };

  const openAttachment = async (d) => {
    try {
      const res = await axios.get(`${API}/biomed-devices/${d.id}/attachment`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("فشل فتح المرفق");
    }
  };

  const removeExistingAttach = async (deviceId) => {
    if (!window.confirm("هل تريد حذف المرفق الحالي؟")) return;
    try {
      await axios.delete(`${API}/biomed-devices/${deviceId}/attachment`, { headers });
      setExistingAttach(null);
      toast.success("تم حذف المرفق");
      load();
    } catch {
      toast.error("فشل حذف المرفق");
    }
  };

  const load = async () => {
    try {
      const [d, c, f, o] = await Promise.all([
        axios.get(`${API}/biomed-devices`, { headers }),
        axios.get(`${API}/clinics`, { headers }),
        axios.get(`${API}/facilities`, { headers }),
        axios.get(`${API}/offices`, { headers }),
      ]);
      setDevices(d.data || []);
      setClinics(Array.isArray(c.data) ? c.data : (c.data?.clinics || []));
      // Only show facilities & offices where the biomed flag is enabled.
      setFacilities((f.data || []).filter(x => x.has_biomed_devices));
      setOffices((o.data || []).filter(x => x.has_biomed_devices));
    } catch {
      toast.error("Failed to load devices");
    }
  };
  useEffect(() => { load(); }, []);

  // Flat list of allowed locations for filter + form dropdowns
  const locationOptions = useMemo(() => ([
    ...clinics.map(c => ({ type: "clinic", id: c.id, name: c.name, floor: c.floor, label: "عيادة" })),
    ...facilities.map(x => ({ type: "facility", id: x.id, name: x.name, floor: x.floor, label: "مرفق" })),
    ...offices.map(x => ({ type: "office", id: x.id, name: x.name, floor: x.floor, label: "مكتب" })),
  ]), [clinics, facilities, offices]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return devices.filter(d => {
      // Filter by active tab (device_type). Legacy docs without device_type = biomed.
      const dt = d.device_type || "biomed";
      if (dt !== activeTab) return false;
      if (filterLocation !== "all") {
        const [t, id] = filterLocation.split(":");
        const devT = d.location_type || (d.clinic_id ? "clinic" : null);
        const devId = d.location_id || d.clinic_id;
        if (devT !== t || devId !== id) return false;
      }
      if (!s) return true;
      return [d.biomed_no, d.device_name, d.manufacturer, d.model_no, d.serial_no, d.location_name, d.clinic_name]
        .some(v => (v || "").toLowerCase().includes(s));
    });
  }, [devices, search, filterLocation, activeTab]);

  const openCreate = async () => {
    setForm({
      ...emptyForm,
      device_type: activeTab,
      electrical_type: activeTab === "electrical" ? "ac" : "",
      electric_type: activeTab === "electric" ? "ac" : "",
      safety_type: activeTab === "safety" ? "fire_extinguisher" : "",
    });
    setAttachFile(null);
    setExistingAttach(null);
    setFormDialog({ mode: "create" });
    try {
      const res = await axios.get(`${API}/biomed-devices/next-number?device_type=${activeTab}`, { headers });
      setForm(f => ({ ...f, biomed_no: res.data.number }));
    } catch { /* backend will generate on save */ }
  };
  const openEdit = (d) => {
    setForm({
      biomed_no: d.biomed_no, device_name: d.device_name, manufacturer: d.manufacturer || "",
      model_no: d.model_no || "", serial_no: d.serial_no || "",
      device_type: d.device_type || "biomed",
      electrical_type: d.electrical_type || "",
      electric_type: d.electric_type || "",
      safety_type: d.safety_type || "",
      device_floor: d.device_floor || "",
      quantity: d.quantity || 1,
      location_type: d.location_type || (d.clinic_id ? "clinic" : "clinic"),
      location_id: d.location_id || d.clinic_id || "",
      location_text: d.location_type === "text" ? (d.location_name || "") : "",
      notes: d.notes || "",
    });
    setAttachFile(null);
    setExistingAttach((d.device_type || "biomed") === "biomed" && d.attachment_path ? { id: d.id, filename: d.attachment_filename } : null);
    setFormDialog({ mode: "edit", device: d });
  };

  const saveDevice = async () => {
    // Base required fields (safety: type + text location suffice)
    const isSafety = form.device_type === "safety";
    const requiredBase = isSafety
      ? []
      : (formDialog?.mode === "create" ? ["device_name","location_id"] : ["biomed_no","device_name","location_id"]);
    for (const f of requiredBase) {
      if (!form[f]) { toast.error(`Field '${f.replace("_"," ")}' is required`); return; }
    }
    if (isSafety && !form.location_text.trim()) {
      toast.error("الموقع مطلوب");
      return;
    }
    // Biomed/Electrical also require manufacturer + model + serial
    if (form.device_type !== "safety") {
      for (const f of ["manufacturer","model_no","serial_no"]) {
        if (!form[f]) { toast.error(`Field '${f.replace("_"," ")}' is required`); return; }
      }
    }
    if (form.device_type === "electrical" && !form.electrical_type) {
      toast.error("Electrical Type is required");
      return;
    }
    if (form.device_type === "electric" && !form.electric_type) {
      toast.error("نوع الجهاز الكهربائي مطلوب");
      return;
    }
    if (form.device_type === "safety" && !form.safety_type) {
      toast.error("Safety Type is required");
      return;
    }
    setSaveBusy(true);
    try {
      const payload = { ...form };
      if (payload.device_type !== "electrical") payload.electrical_type = null;
      if (payload.device_type !== "electric") payload.electric_type = null;
      if (payload.device_type !== "safety") { payload.safety_type = null; payload.location_text = null; }
      if (payload.device_type === "safety") {
        payload.device_name = (typeLabel(safetyTypes, SAFETY_TYPE_LABELS, payload.safety_type)).split(" / ").pop();
        payload.quantity = Math.max(1, parseInt(payload.quantity, 10) || 1);
        delete payload.location_type;
        delete payload.location_id;
      }
      let savedId;
      if (formDialog.mode === "create") {
        payload.biomed_no = null;
        const res = await axios.post(`${API}/biomed-devices`, payload, { headers });
        savedId = res.data.id;
        toast.success("تمت إضافة الجهاز");
      } else {
        savedId = formDialog.device.id;
        await axios.put(`${API}/biomed-devices/${savedId}`, payload, { headers });
        toast.success("Device updated");
      }
      if (attachFile && savedId && form.device_type === "biomed") {
        const fd = new FormData();
        fd.append("file", attachFile);
        try {
          await axios.post(`${API}/biomed-devices/${savedId}/attachment`, fd, { headers });
          toast.success("تم رفع المرفق");
        } catch (e) {
          toast.error(e.response?.data?.detail || "فشل رفع المرفق");
        }
      }
      setFormDialog(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaveBusy(false);
    }
  };

  const deleteDevice = async (device) => {
    if (!window.confirm(`هل أنت متأكد من حذف الجهاز "${device.biomed_no} — ${device.device_name}"؟\nلا يمكن التراجع.`)) return;
    try {
      await axios.delete(`${API}/biomed-devices/${device.id}`, { headers });
      toast.success("تم حذف الجهاز");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحذف");
    }
  };

  const [evalReportFile, setEvalReportFile] = useState(null);
  const [evalBusy, setEvalBusy] = useState(false);
  const [editEvalCtx, setEditEvalCtx] = useState(null); // evaluation entry being edited

  const openEditEval = (device, ev) => {
    setEvalDialog(device);
    setEditEvalCtx(ev);
    setEvalForm({
      status: ev.status || "operational",
      condition_score: ev.condition_score ?? 5,
      notes: ev.notes || "",
      evaluation_date: (ev.evaluated_at || "").slice(0, 10),
    });
    setEvalReportFile(null);
  };

  const onEvalReportSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("يجب أن يكون الملف بصيغة PDF"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الملف يجب أن يكون أقل من 10MB"); return; }
    setEvalReportFile(file);
  };

  const openEvalReport = async (deviceId, evalId) => {
    try {
      const res = await axios.get(`${API}/biomed-devices/${deviceId}/evaluations/${evalId}/report`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("فشل فتح تقرير الوكيل");
    }
  };

  const nextEvalDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 180);
    return d.toLocaleDateString("en-CA");
  };

  const submitEvaluation = async () => {
    if (!evalForm.evaluation_date) { toast.error("تاريخ التقييم مطلوب"); return; }
    setEvalBusy(true);
    try {
      let res;
      if (editEvalCtx) {
        res = await axios.put(`${API}/biomed-devices/${evalDialog.id}/evaluations/${editEvalCtx.id}`, evalForm, { headers });
      } else {
        res = await axios.post(`${API}/biomed-devices/${evalDialog.id}/evaluations`, evalForm, { headers });
      }
      const evals = res.data?.evaluations || [];
      const targetEvalId = editEvalCtx ? editEvalCtx.id : evals[evals.length - 1]?.id;
      if (evalReportFile && targetEvalId) {
        const fd = new FormData();
        fd.append("file", evalReportFile);
        try {
          const rep = await axios.post(`${API}/biomed-devices/${evalDialog.id}/evaluations/${targetEvalId}/report`, fd, { headers });
          res = rep;
          toast.success(editEvalCtx ? "تم تعديل التقييم ورفع التقرير" : "تم حفظ التقييم ورفع تقرير الوكيل");
        } catch (e) {
          toast.error(e.response?.data?.detail || "حُفظ التقييم لكن فشل رفع التقرير");
        }
      } else {
        toast.success(editEvalCtx ? "تم تعديل التقييم" : "تم حفظ التقييم");
      }
      if (detail?.id === evalDialog.id) setDetail(res.data);
      setEvalDialog(null); setEvalForm(emptyEval); setEvalReportFile(null); setEditEvalCtx(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setEvalBusy(false);
    }
  };

  const statusBadge = (status) => {
    if (!status) return <Badge variant="outline">—</Badge>;
    const m = STATUS_META[status]; if (!m) return <Badge>{status}</Badge>;
    const Icon = m.icon;
    return <Badge className={`${m.cls} border-0 gap-1`}><Icon className="w-3 h-3" />{m.label}</Badge>;
  };

  return (
    <div className="space-y-4" dir="rtl" data-testid="biomed-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#655dc6] text-white flex items-center justify-center">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#4a3fa8]">الأجهزة الكهربائية والطبية</h2>
            <p className="text-sm text-slate-500">Electrical &amp; Medical Devices Inventory</p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {activeTab !== "hazmat" && (
              <Button
                variant="outline"
                onClick={() => setBulkPrint(true)}
                disabled={filtered.length === 0}
                className="border-[#655dc6] text-[#655dc6] hover:bg-[#F3F2FA] gap-1.5"
                data-testid="bulk-barcode-btn"
              >
                <Barcode className="w-4 h-4" /> طباعة الباركود
              </Button>
            )}
            {(activeTab === "electrical" || activeTab === "electric") && (
              <Button
                variant="outline"
                onClick={() => setBulkPPM(true)}
                disabled={filtered.length === 0}
                className="border-teal-600 text-teal-700 hover:bg-teal-50 gap-1.5"
                data-testid="bulk-ppm-btn"
              >
                <Tag className="w-4 h-4" /> طباعة ملصقات PPM
              </Button>
            )}
            <Button onClick={() => activeTab === "hazmat" ? setHazmatAddSignal(s => s + 1) : openCreate()} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="add-device-btn">
              <PlusCircle className="w-4 h-4" /> {activeTab === "hazmat" ? "إضافة مادة" : "إضافة جهاز"}
            </Button>
          </div>
        )}
      </div>

      {/* Device Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} dir="rtl">
        <TabsList className="me-auto">
          <TabsTrigger value="biomed" data-testid="tab-biomed" className="gap-1.5">
            <HeartPulse className="w-4 h-4" /> الأجهزة الطبية الحيوية
          </TabsTrigger>
          <TabsTrigger value="electrical" data-testid="tab-electrical" className="gap-1.5">
            <MonitorSmartphone className="w-4 h-4" /> الأجهزة الإلكترونية
          </TabsTrigger>
          <TabsTrigger value="electric" data-testid="tab-electric" className="gap-1.5">
            <Zap className="w-4 h-4" /> الأجهزة الكهربائية
          </TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety" className="gap-1.5">
            <ShieldAlert className="w-4 h-4" /> أجهزة السلامة
          </TabsTrigger>
          <TabsTrigger value="hazmat" data-testid="tab-hazmat" className="gap-1.5">
            <FlaskConical className="w-4 h-4" /> المواد الكيميائية
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {!canManage && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            وضع القراءة فقط — لا تملك صلاحية إضافة أجهزة. اطلب من الأدمن منحك &quot;صلاحية الأجهزة&quot; لحسابك.
          </CardContent>
        </Card>
      )}

      {activeTab === "hazmat" ? (
        <HazmatSection canManage={canManage} isAdmin={isAdmin} addSignal={hazmatAddSignal} />
      ) : (
      <>
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white border rounded-md px-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالرقم / الاسم / الشركة / الموديل / السيريال"
            className="border-0 focus-visible:ring-0 text-right" data-testid="search-input" />
        </div>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="max-w-xs" data-testid="filter-location"><SelectValue placeholder="جميع المواقع" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع المواقع</SelectItem>
            {locationOptions.map(l => (
              <SelectItem key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>
                [{l.label}] {l.name}{l.floor ? ` — ${l.floor}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">{(activeTab === "electrical" || activeTab === "electric") ? "Asset No." : activeTab === "safety" ? "رقم الجهاز" : "BioMed No."}</TableHead>
              <TableHead className="text-right">Device Name</TableHead>
              {activeTab !== "safety" && (
                <>
                  <TableHead className="text-right">Manufacturer</TableHead>
                  <TableHead className="text-right">Model No.</TableHead>
                  <TableHead className="text-right">Serial No.</TableHead>
                </>
              )}
              {activeTab === "safety" && (
                <>
                  <TableHead className="text-right">النوع</TableHead>
                  <TableHead className="text-right">الدور</TableHead>
                  <TableHead className="text-right">العدد</TableHead>
                </>
              )}
              <TableHead className="text-right">الموقع</TableHead>
              <TableHead className="text-right">آخر تقييم</TableHead>
              <TableHead className="text-right">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={activeTab === "safety" ? 8 : 8} className="text-center text-slate-400 py-8">لا توجد أجهزة مسجّلة</TableCell></TableRow>
            )}
            {filtered.map(d => (
              <TableRow key={d.id} data-testid={`row-${d.biomed_no}`}>
                <TableCell className="font-mono font-semibold text-[#4a3fa8]">{d.biomed_no}</TableCell>
                <TableCell className="font-medium">{d.device_name}</TableCell>
                {activeTab !== "safety" && (
                  <>
                    <TableCell>{d.manufacturer}</TableCell>
                    <TableCell className="font-mono text-xs">{d.model_no}</TableCell>
                    <TableCell className="font-mono text-xs">{d.serial_no}</TableCell>
                  </>
                )}
                {activeTab === "safety" && (
                  <>
                    <TableCell className="text-xs">{typeLabel(safetyTypes, SAFETY_TYPE_LABELS, d.safety_type).split(" / ").pop() || "—"}</TableCell>
                    <TableCell className="text-xs">{d.device_floor || "—"}</TableCell>
                    <TableCell className="font-bold text-[#655dc6]">{d.quantity || 1}</TableCell>
                  </>
                )}
                <TableCell>
                  {d.location_type && (
                    <Badge variant="outline" className="mr-1 text-[10px]">
                      {d.location_type === "clinic" ? "عيادة" : d.location_type === "facility" ? "مرفق" : "مكتب"}
                    </Badge>
                  )}
                  {d.location_name || d.clinic_name}
                  {(d.location_floor || d.clinic_floor) && <span className="text-slate-400 text-xs mr-1">— الطابق {d.location_floor || d.clinic_floor}</span>}
                </TableCell>
                <TableCell>{statusBadge(d.latest_status)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(d)} data-testid={`view-${d.biomed_no}`}>عرض</Button>
                  {activeTab === "biomed" && d.attachment_path && (
                    <Button size="sm" variant="ghost" onClick={() => openAttachment(d)} title="عرض المرفق PDF" className="text-emerald-700 hover:bg-emerald-50" data-testid={`attach-${d.biomed_no}`}>
                      <FileText className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setBarcodeDevice(d)} title="ملصق الباركود" data-testid={`barcode-${d.biomed_no}`}>
                    <Barcode className="w-4 h-4" />
                  </Button>
                  {(activeTab === "electrical" || activeTab === "electric") && (
                    <Button size="sm" variant="ghost" onClick={() => setPpmDevice(d)} title="ملصق PPM (4×2.2 سم)" className="text-teal-700 hover:bg-teal-50" data-testid={`ppm-${d.biomed_no}`}>
                      <Tag className="w-4 h-4" />
                    </Button>
                  )}
                  <Link to={`/biomed-devices/${d.id}/print`} target="_blank">
                    <Button size="sm" variant="ghost" title="Print evaluation report" data-testid={`print-${d.biomed_no}`}>
                      <Printer className="w-4 h-4" />
                    </Button>
                  </Link>
                  {canManage && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-${d.biomed_no}`}><Pencil className="w-4 h-4" /></Button>
                      {activeTab !== "safety" && (
                        <Button size="sm" variant="ghost" onClick={() => { setTransferDialog(d); setTransferForm({ target: "", notes: "" }); }} title="نقل الجهاز لموقع آخر" className="text-sky-600 hover:bg-sky-50" data-testid={`transfer-${d.biomed_no}`}>
                          <ArrowLeftRight className="w-4 h-4" />
                        </Button>
                      )}
                      {activeTab !== "safety" && (
                        <Button size="sm" variant="ghost" onClick={() => { setReplaceDialog(d); setReplaceForm({ ...emptyReplaceForm, old_serial: d.serial_no || "" }); }} title="إحلال / تجديد الجهاز" className="text-amber-600 hover:bg-amber-50" data-testid={`replace-${d.biomed_no}`}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      {d.can_evaluate_now === false ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled
                          className="border-slate-300 text-slate-500 cursor-not-allowed ms-auto w-[150px] justify-center text-xs"
                          title={`Semi-annual eval — next available on ${d.next_evaluation_available_at ? new Date(d.next_evaluation_available_at).toLocaleDateString("en-CA") : "—"}`}
                          data-testid={`eval-locked-${d.biomed_no}`}
                        >
                          <Lock className="w-3.5 h-3.5 mr-1" /> Next {d.next_evaluation_available_at ? new Date(d.next_evaluation_available_at).toLocaleDateString("en-CA") : ""}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => { setEvalDialog(d); setEvalForm(emptyEval); setEvalReportFile(null); }} className="border-emerald-300 text-emerald-700 ms-auto w-[150px] justify-center" data-testid={`eval-${d.biomed_no}`}>
                          <ClipboardList className="w-3.5 h-3.5 mr-1" /> تقييم
                        </Button>
                      )}
                    </>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteDevice(d)}
                      className={`text-red-600 hover:bg-red-50 hover:text-red-700${canManage ? "" : " ms-auto"}`}
                      title="حذف الجهاز (Admin only)"
                      data-testid={`delete-${d.biomed_no}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </>
      )}

      {/* Create / Edit dialog */}
      <DeviceBarcodeLabel device={barcodeDevice} onClose={() => setBarcodeDevice(null)} />
      <PPMStickerLabel device={ppmDevice} onClose={() => setPpmDevice(null)} />
      {bulkPrint && <BulkBarcodePrint devices={filtered} onClose={() => setBulkPrint(false)} />}
      {bulkPPM && <BulkPPMPrint devices={filtered} onClose={() => setBulkPPM(false)} />}
      <Dialog open={!!formDialog} onOpenChange={(o) => !o && setFormDialog(null)}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {formDialog?.mode === "create"
                ? (activeTab === "electrical" ? "إضافة جهاز إلكتروني" : activeTab === "electric" ? "إضافة جهاز كهربائي" : activeTab === "safety" ? "إضافة جهاز سلامة" : "إضافة جهاز طبي")
                : "تعديل الجهاز"}
            </DialogTitle>
            <DialogDescription>سجّل بيانات الجهاز كما وردت من الشركة المصنّعة.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {form.device_type === "electrical" && (
              <div className="space-y-1 col-span-2">
                <div className="flex items-center justify-between">
                  <Label>نوع الجهاز الإلكتروني *</Label>
                  {isAdmin && <LookupManager lookupKey="electrical_types" title="إدارة أنواع الأجهزة الإلكترونية" token={token} onChanged={loadTypeLookups} dir="rtl" placeholder="مثال: Scanner / ماسح ضوئي" addLabel="إضافة" />}
                </div>
                <Select value={form.electrical_type} onValueChange={(v) => setForm({ ...form, electrical_type: v })}>
                  <SelectTrigger data-testid="f-electrical-type"><SelectValue placeholder="اختر النوع" /></SelectTrigger>
                  <SelectContent>
                    {electricalTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.device_type === "electric" && (
              <div className="space-y-1 col-span-2">
                <div className="flex items-center justify-between">
                  <Label>نوع الجهاز الكهربائي *</Label>
                  {isAdmin && <LookupManager lookupKey="electric_types" title="إدارة أنواع الأجهزة الكهربائية" token={token} onChanged={loadTypeLookups} dir="rtl" placeholder="مثال: Generator / مولد" addLabel="إضافة" />}
                </div>
                <Select value={form.electric_type} onValueChange={(v) => setForm({ ...form, electric_type: v })}>
                  <SelectTrigger data-testid="f-electric-type"><SelectValue placeholder="اختر النوع" /></SelectTrigger>
                  <SelectContent>
                    {electricTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.device_type === "safety" && (
              <div className="space-y-1 col-span-2">
                <div className="flex items-center justify-between">
                  <Label>نوع جهاز السلامة *</Label>
                  {isAdmin && <LookupManager lookupKey="safety_types" title="إدارة أنواع أجهزة السلامة" token={token} onChanged={loadTypeLookups} dir="rtl" placeholder="مثال: Gas Detector / كاشف غاز" addLabel="إضافة" />}
                </div>
                <Select value={form.safety_type} onValueChange={(v) => setForm({ ...form, safety_type: v })}>
                  <SelectTrigger data-testid="f-safety-type"><SelectValue placeholder="اختر نوع الجهاز" /></SelectTrigger>
                  <SelectContent>
                    {safetyTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label>رقم الجهاز *</Label>
              <Input value={form.biomed_no} readOnly className="text-right bg-slate-50 font-mono text-[#655dc6] font-bold" data-testid="f-biomed-no" />
              <p className="text-[10px] text-slate-400">يتولد إلكترونياً — لا يمكن تكراره</p>
            </div>
            {form.device_type !== "safety" && (
              <div className="space-y-1"><Label>اسم الجهاز *</Label><Input value={form.device_name} onChange={(e) => setForm({ ...form, device_name: e.target.value })} className="text-right" data-testid="f-name" /></div>
            )}
            {form.device_type !== "safety" && (
              <>
                <div className="space-y-1"><Label>الشركة المصنعة *</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="text-right" data-testid="f-manufacturer" /></div>
                <div className="space-y-1"><Label>الموديل *</Label><Input value={form.model_no} onChange={(e) => setForm({ ...form, model_no: e.target.value })} className="text-right" data-testid="f-model" /></div>
                <div className="space-y-1 col-span-2"><Label>الرقم التسلسلي *</Label><Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} className="text-right" data-testid="f-serial" /></div>
              </>
            )}
            {form.device_type === "safety" && (
              <>
                <div className="space-y-1"><Label>الدور</Label><Input value={form.device_floor} onChange={(e) => setForm({ ...form, device_floor: e.target.value })} placeholder="مثال: الأول / الأرضي / 2" className="text-right" data-testid="f-device-floor" /></div>
                <div className="space-y-1"><Label>العدد *</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="text-right" data-testid="f-quantity" /></div>
              </>
            )}
            <div className="space-y-1 col-span-2">
              <Label>الموقع *</Label>
              {form.device_type === "safety" ? (
                <Input
                  value={form.location_text}
                  onChange={(e) => setForm({ ...form, location_text: e.target.value })}
                  placeholder="مثال: ممر الدور الأول بجوار عيادة 5"
                  className="text-right"
                  data-testid="f-location-text"
                />
              ) : (
              <Select
                value={form.location_id ? `${form.location_type}:${form.location_id}` : ""}
                onValueChange={(v) => {
                  const [t, id] = v.split(":");
                  setForm({ ...form, location_type: t, location_id: id });
                }}
              >
                <SelectTrigger data-testid="f-location"><SelectValue placeholder="اختر الموقع (عيادة / مرفق / مكتب)" /></SelectTrigger>
                <SelectContent>{locationOptions.map(l => (
                  <SelectItem key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>
                    [{l.label}] {l.name}{l.floor ? ` — ${l.floor}` : ""}
                  </SelectItem>
                ))}</SelectContent>
              </Select>
              )}
              {form.device_type !== "safety" && (
                <p className="text-xs text-slate-400">
                  * لعرض مرفق أو مكتب هنا يجب تفعيل علامة الأجهزة الطبية له من صفحته
                </p>
              )}
            </div>
            {form.device_type === "biomed" && (
              <div className="space-y-1 col-span-2">
                <Label>مرفق الجهاز (PDF)</Label>
                {existingAttach && !attachFile && (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 text-emerald-700 truncate"><FileText className="w-4 h-4 shrink-0" /> {existingAttach.filename || "مرفق PDF"}</span>
                    <button type="button" onClick={() => removeExistingAttach(existingAttach.id)} className="text-red-500 hover:text-red-700" data-testid="dev-attach-remove-existing">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {attachFile ? (
                  <div className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 text-slate-700 truncate"><FileText className="w-4 h-4 shrink-0 text-[#655dc6]" /> {attachFile.name}</span>
                    <button type="button" onClick={() => setAttachFile(null)} className="text-red-500 hover:text-red-700" data-testid="dev-attach-clear">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[#655dc6] text-sm text-slate-500">
                    <Upload className="w-4 h-4" /> {existingAttach ? "استبدال المرفق (PDF حتى 10MB)" : "اختر ملف PDF (حتى 10MB)"}
                    <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onAttachSelect} data-testid="dev-attach-input" />
                  </label>
                )}
              </div>
            )}
            <div className="space-y-1 col-span-2"><Label>ملاحظات</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-right min-h-[70px]" data-testid="f-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDialog(null)}>إلغاء</Button>
            <Button onClick={saveDevice} disabled={saveBusy} className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="save-device-btn">{saveBusy ? "جارٍ الحفظ..." : "حفظ"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replacement / Renewal dialog */}
      <Dialog open={!!replaceDialog} onOpenChange={(o) => !o && setReplaceDialog(null)}>
        <DialogContent dir="rtl" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-amber-600" /> إحلال / تجديد — {replaceDialog?.device_name}</DialogTitle>
            <DialogDescription>رقم الجهاز: <span className="font-mono">{replaceDialog?.biomed_no}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>نوع العملية *</Label>
                <Select value={replaceForm.rtype} onValueChange={(v) => setReplaceForm({ ...replaceForm, rtype: v })}>
                  <SelectTrigger data-testid="rep-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replacement">إحلال — استبدال الجهاز</SelectItem>
                    <SelectItem value="renewal">تجديد — تحديث / إصلاح شامل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>التاريخ *</Label>
                <Input type="date" value={replaceForm.date} onChange={(e) => setReplaceForm({ ...replaceForm, date: e.target.value })} className="text-right" data-testid="rep-date" />
              </div>
              <div className="space-y-1">
                <Label>السيريال القديم</Label>
                <Input value={replaceForm.old_serial} onChange={(e) => setReplaceForm({ ...replaceForm, old_serial: e.target.value })} className="font-mono" placeholder="Old Serial No." data-testid="rep-old-serial" />
              </div>
              <div className="space-y-1">
                <Label>السيريال الجديد {replaceForm.rtype === "replacement" ? "*" : "(اختياري)"}</Label>
                <Input value={replaceForm.new_serial} onChange={(e) => setReplaceForm({ ...replaceForm, new_serial: e.target.value })} className="font-mono" placeholder="Serial No." data-testid="rep-new-serial" />
              </div>
              <div className="space-y-1">
                <Label>الموديل الجديد (اختياري)</Label>
                <Input value={replaceForm.new_model} onChange={(e) => setReplaceForm({ ...replaceForm, new_model: e.target.value })} className="font-mono" placeholder={replaceDialog?.model_no ? `الحالي: ${replaceDialog.model_no}` : "Model No."} data-testid="rep-new-model" />
              </div>
              <div className="space-y-1">
                <Label>الشركة المصنعة الجديدة (اختياري)</Label>
                <Input value={replaceForm.new_manufacturer} onChange={(e) => setReplaceForm({ ...replaceForm, new_manufacturer: e.target.value })} placeholder={replaceDialog?.manufacturer ? `الحالية: ${replaceDialog.manufacturer}` : "Manufacturer"} data-testid="rep-new-manufacturer" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>السبب *</Label>
              <Textarea value={replaceForm.reason} onChange={(e) => setReplaceForm({ ...replaceForm, reason: e.target.value })}
                placeholder="مثال: انتهاء العمر الافتراضي، تكرار الأعطال، تحديث تقني..."
                className="text-right min-h-[70px]" data-testid="rep-reason" />
            </div>
            <Button onClick={submitReplacement} disabled={replaceBusy} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="rep-save-btn">
              {replaceBusy ? "جارٍ التوثيق..." : "توثيق العملية"}
            </Button>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase text-slate-500">سجل الإحلال والتجديد ({replaceDialog?.replacements?.length || 0})</p>
                {replaceDialog?.replacements?.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setRepPrintItem(replaceDialog)} className="gap-1.5 text-xs h-7" data-testid="rep-print-btn">
                    <Printer className="w-3.5 h-3.5" /> طباعة السجل A4
                  </Button>
                )}
              </div>
              {(!replaceDialog?.replacements || replaceDialog.replacements.length === 0) ? (
                <p className="text-sm text-slate-400 text-center py-3">لا توجد عمليات موثقة بعد</p>
              ) : (
                <div className="space-y-2 max-h-[220px] overflow-y-auto">
                  {[...replaceDialog.replacements].reverse().map(r => (
                    <div key={r.id} className="border rounded-md p-2.5 text-xs bg-slate-50" data-testid={`rep-history-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={`border-0 ${r.rtype === "replacement" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>
                          {r.rtype === "replacement" ? "إحلال" : "تجديد"}
                        </Badge>
                        <span className="text-slate-500 font-mono">{r.date}</span>
                      </div>
                      <p className="text-slate-700 mb-1">{r.reason}</p>
                      {(r.old_serial || r.new_serial) && (
                        <p className="font-mono text-[11px] text-slate-600" dir="ltr">
                          {r.old_serial || "—"} ← {r.new_serial || "—"}
                        </p>
                      )}
                      {r.new_model && (
                        <p className="text-[11px] text-slate-600">الموديل: <span className="font-mono" dir="ltr">{r.old_model || "—"} ← {r.new_model}</span></p>
                      )}
                      {r.new_manufacturer && (
                        <p className="text-[11px] text-slate-600">الشركة: {r.old_manufacturer || "—"} ← {r.new_manufacturer}</p>
                      )}
                      <p className="text-slate-400 mt-1">بواسطة: {r.created_by_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Replacement log A4 print dialog */}
      <Dialog open={!!repPrintItem} onOpenChange={(o) => !o && setRepPrintItem(null)}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto rep-print-dialog">
          <DialogHeader>
            <DialogTitle>طباعة سجل الإحلال والتجديد</DialogTitle>
            <DialogDescription>ورقة A4 بسجل عمليات الجهاز الموثقة</DialogDescription>
          </DialogHeader>
          <style>{`
            .rep-print-sheet { background: #fff; border: 1px solid #E5E7EB; border-radius: 8px; padding: 20px; direction: rtl; }
            .rep-print-sheet .rp-head { display: flex; flex-direction: column; align-items: center; gap: 3px; border-bottom: 2px solid #0A0A0A; padding-bottom: 10px; margin-bottom: 14px; }
            .rep-print-sheet .rp-head img { height: 44px; }
            .rep-print-sheet .rp-head .co { font-weight: 700; font-size: 13px; color: #0A0A0A; }
            .rep-print-sheet .rp-title { text-align: center; font-weight: 700; font-size: 15px; margin-bottom: 12px; color: #0A0A0A; }
            .rep-print-sheet table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 14px; }
            .rep-print-sheet td, .rep-print-sheet th { border: 1px solid #D1D5DB; padding: 6px 8px; }
            .rep-print-sheet td.k { background: #F3F4F6; font-weight: 700; width: 28%; color: #0A0A0A; }
            .rep-print-sheet th { background: #F3F4F6; font-weight: 700; color: #0A0A0A; text-align: right; }
            .rep-print-sheet .mono { font-family: 'Courier New', monospace; direction: ltr; text-align: left; }
            .rep-print-sheet .rp-sec { font-weight: 700; font-size: 13px; margin: 10px 0 6px; color: #0A0A0A; }
            .rep-print-sheet .rp-foot { margin-top: 14px; font-size: 10px; color: #6B7280; display: flex; justify-content: space-between; }
            @media print {
              @page { size: A4 portrait; margin: 12mm; }
              #root { display: none !important; }
              html, body { margin: 0 !important; padding: 0 !important; }
              body * { visibility: hidden !important; }
              .rep-print-sheet, .rep-print-sheet * { visibility: visible !important; }
              .rep-print-dialog {
                position: static !important;
                transform: none !important;
                width: 100% !important;
                max-width: 100% !important;
                max-height: none !important;
                overflow: visible !important;
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
              }
              .rep-print-dialog > *:not(.rep-print-sheet):not(style) { display: none !important; }
              .rep-print-sheet { border: none !important; border-radius: 0 !important; padding: 0 !important; }
              .rep-print-sheet .rp-head img { height: 60px; }
              .rep-print-sheet .rp-head .co { font-size: 15px; }
              .rep-print-sheet .rp-title { font-size: 18px; margin: 14px 0 16px; }
              .rep-print-sheet table { font-size: 13.5px; }
              .rep-print-sheet td, .rep-print-sheet th { padding: 9px 10px; }
              .rep-print-sheet .rp-sec { font-size: 15px; }
              .rep-print-sheet .rp-foot { margin-top: 20px; font-size: 11px; }
              .rep-print-sheet tr { page-break-inside: avoid; }
            }
          `}</style>
          {repPrintItem && (
            <div className="rep-print-sheet" data-testid="rep-print-sheet">
              <div className="rp-head">
                <img src="/lucent-logo.png" alt="Lucent" />
                <div className="co">Lucent Dental Center Co.,</div>
              </div>
              <div className="rp-title">سجل إحلال وتجديد جهاز — Device Replacement & Renewal Log</div>
              <table>
                <tbody>
                  <tr><td className="k">رقم الجهاز</td><td className="mono" style={{ fontWeight: 700 }}>{repPrintItem.biomed_no}</td></tr>
                  <tr><td className="k">اسم الجهاز</td><td>{repPrintItem.device_name}</td></tr>
                  <tr><td className="k">الشركة المصنعة</td><td>{repPrintItem.manufacturer || "—"}</td></tr>
                  <tr><td className="k">الموديل</td><td className="mono">{repPrintItem.model_no || "—"}</td></tr>
                  <tr><td className="k">السيريال الحالي</td><td className="mono" style={{ fontWeight: 700 }}>{repPrintItem.serial_no || "—"}</td></tr>
                  <tr><td className="k">الموقع</td><td>{repPrintItem.location_name || repPrintItem.clinic_name || "—"}</td></tr>
                </tbody>
              </table>
              <div className="rp-sec">العمليات الموثقة ({repPrintItem.replacements?.length || 0})</div>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>النوع</th>
                    <th>التاريخ</th>
                    <th>السبب</th>
                    <th>السيريال القديم</th>
                    <th>السيريال الجديد</th>
                    <th>الموديل / الشركة</th>
                    <th>بواسطة</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(repPrintItem.replacements || [])].reverse().map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 700 }}>{r.rtype === "replacement" ? "إحلال" : "تجديد"}</td>
                      <td className="mono">{r.date}</td>
                      <td>{r.reason}</td>
                      <td className="mono">{r.old_serial || "—"}</td>
                      <td className="mono">{r.new_serial || "—"}</td>
                      <td>
                        {r.new_model ? <div className="mono" dir="ltr">{r.old_model || "—"} ← {r.new_model}</div> : null}
                        {r.new_manufacturer ? <div>{r.old_manufacturer || "—"} ← {r.new_manufacturer}</div> : null}
                        {!r.new_model && !r.new_manufacturer ? "—" : null}
                      </td>
                      <td>{r.created_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="rp-foot">
                <span>© شركة مركز لوسنت للأسنان — سجل إحلال وتجديد الأجهزة</span>
                <span>{new Date().toLocaleDateString("ar-SA")}</span>
              </div>
            </div>
          )}
          <Button onClick={() => window.print()} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="rep-print-confirm-btn">
            <Printer className="w-4 h-4" /> طباعة
          </Button>
        </DialogContent>
      </Dialog>

      {/* Evaluation dialog */}
      <Dialog open={!!evalDialog} onOpenChange={(o) => { if (!o) { setEvalDialog(null); setEditEvalCtx(null); } }}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>{editEvalCtx ? "تعديل تقييم سابق" : "تقييم دوري"} — {evalDialog?.device_name}</DialogTitle>
            <DialogDescription>BioMed No.: <span className="font-mono">{evalDialog?.biomed_no}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>الحالة *</Label>
              <Select value={evalForm.status} onValueChange={(v) => setEvalForm({ ...evalForm, status: v })}>
                <SelectTrigger data-testid="eval-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operational">يعمل بكفاءة</SelectItem>
                  <SelectItem value="needs_maintenance">يحتاج صيانة</SelectItem>
                  <SelectItem value="out_of_service">خارج الخدمة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>تاريخ التقييم *</Label>
                <Input type="date" value={evalForm.evaluation_date}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setEvalForm({ ...evalForm, evaluation_date: e.target.value })}
                  className="text-right" data-testid="eval-date" />
              </div>
              <div className="space-y-1">
                <Label>التقييم القادم (تلقائي)</Label>
                <Input value={nextEvalDate(evalForm.evaluation_date)} readOnly className="bg-slate-50 font-mono text-slate-600" data-testid="eval-next-date" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>درجة الحالة (1-5)</Label>
              <Input type="number" min={1} max={5} value={evalForm.condition_score}
                onChange={(e) => setEvalForm({ ...evalForm, condition_score: Number(e.target.value) })}
                className="text-right" data-testid="eval-score" />
            </div>
            <div className="space-y-1"><Label>ملاحظات</Label>
              <Textarea value={evalForm.notes} onChange={(e) => setEvalForm({ ...evalForm, notes: e.target.value })}
                placeholder="ملاحظات الفحص، الأعطال الملاحظة، القطع المستهلكة..."
                className="text-right min-h-[80px]" data-testid="eval-notes" />
            </div>
            <div className="space-y-1">
              <Label>تقرير الوكيل (PDF — اختياري)</Label>
              {evalReportFile ? (
                <div className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 text-slate-700 truncate"><FileText className="w-4 h-4 shrink-0 text-[#655dc6]" /> {evalReportFile.name}</span>
                  <button type="button" onClick={() => setEvalReportFile(null)} className="text-red-500 hover:text-red-700" data-testid="eval-report-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[#655dc6] text-sm text-slate-500">
                  <Upload className="w-4 h-4" /> {editEvalCtx?.report_path ? "استبدال تقرير الوكيل (PDF حتى 10MB)" : "اختر ملف PDF (حتى 10MB)"}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onEvalReportSelect} data-testid="eval-report-input" />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvalDialog(null)}>إلغاء</Button>
            <Button onClick={submitEvaluation} disabled={evalBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-eval-btn">{evalBusy ? "جارٍ الحفظ..." : "حفظ التقييم"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog with evaluation history */}
      {/* Transfer device dialog */}
      <Dialog open={!!transferDialog} onOpenChange={(o) => !o && setTransferDialog(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-sky-600" /> نقل الجهاز — {transferDialog?.device_name}</DialogTitle>
            <DialogDescription>
              الموقع الحالي: {transferDialog?.location_name || transferDialog?.clinic_name || "—"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>الموقع الجديد *</Label>
              <Select value={transferForm.target} onValueChange={(v) => setTransferForm({ ...transferForm, target: v })}>
                <SelectTrigger data-testid="transfer-target"><SelectValue placeholder="اختر العيادة / المرفق / المكتب" /></SelectTrigger>
                <SelectContent>
                  {locationOptions
                    .filter(l => !(l.type === transferDialog?.location_type && l.id === transferDialog?.location_id))
                    .map(l => (
                      <SelectItem key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>
                        {l.label} — {l.name}{l.floor ? ` (الطابق ${l.floor})` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>سبب / ملاحظات النقل</Label>
              <Input value={transferForm.notes} onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })} placeholder="اختياري" data-testid="transfer-notes" />
            </div>
            <Button onClick={submitTransfer} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="transfer-submit">
              <ArrowLeftRight className="w-4 h-4 ml-2" /> نقل الجهاز
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent dir="rtl" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detail?.device_name}</DialogTitle>
            <DialogDescription>BioMed No.: <span className="font-mono">{detail?.biomed_no}</span></DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Manufacturer:</span> <span className="font-medium">{detail.manufacturer}</span></div>
                <div><span className="text-slate-500">Model No.:</span> <span className="font-mono">{detail.model_no}</span></div>
                <div><span className="text-slate-500">Serial No.:</span> <span className="font-mono">{detail.serial_no}</span></div>
                <div><span className="text-slate-500">الموقع:</span> <span className="font-medium">{detail.location_name || detail.clinic_name}</span></div>
                <div className="col-span-2"><span className="text-slate-500">أُدخل بواسطة:</span> <span className="font-medium">{detail.created_by_name}</span></div>
                {detail.notes && <div className="col-span-2 text-xs text-slate-600 italic">{detail.notes}</div>}
              </div>
              {detail.transfer_history?.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-slate-500 mb-2">سجل التنقلات ({detail.transfer_history.length})</p>
                  <div className="space-y-2">
                    {[...detail.transfer_history].reverse().map((tr, i) => (
                      <div key={tr.id || i} className="border rounded-md p-2.5 text-xs bg-sky-50/50" data-testid={`transfer-entry-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sky-800">من: {tr.from_name || "—"} ← إلى: {tr.to_name}</span>
                          <span className="text-slate-500 font-mono">{(tr.transferred_at || "").slice(0, 10)}</span>
                        </div>
                        {tr.notes && <p className="text-slate-700">{tr.notes}</p>}
                        <p className="text-slate-400 mt-1">بواسطة: {tr.transferred_by_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.replacements?.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-slate-500 mb-2">سجل الإحلال والتجديد ({detail.replacements.length})</p>
                  <div className="space-y-2">
                    {[...detail.replacements].reverse().map(r => (
                      <div key={r.id} className="border rounded-md p-2.5 text-xs bg-amber-50/50">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={`border-0 ${r.rtype === "replacement" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>
                            {r.rtype === "replacement" ? "إحلال" : "تجديد"}
                          </Badge>
                          <span className="text-slate-500 font-mono">{r.date}</span>
                        </div>
                        <p className="text-slate-700">{r.reason}</p>
                        {(r.old_serial || r.new_serial) && (
                          <p className="font-mono text-[11px] text-slate-600 mt-1" dir="ltr">{r.old_serial || "—"} ← {r.new_serial || "—"}</p>
                        )}
                        <p className="text-slate-400 mt-1">بواسطة: {r.created_by_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs uppercase text-slate-500 mb-2">سجلّ التقييمات الدورية ({detail.evaluations?.length || 0})</p>
                {(!detail.evaluations || detail.evaluations.length === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-4">لا توجد تقييمات بعد</p>
                ) : (
                  <div className="space-y-2">
                    {[...detail.evaluations].reverse().map(ev => (
                      <div key={ev.id} className="border rounded-md p-3 bg-slate-50 space-y-1">
                        <div className="flex items-center justify-between">
                          {statusBadge(ev.status)}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">{new Date(ev.evaluated_at).toLocaleDateString("en-CA")}</span>
                            {canManage && (
                              <button type="button" onClick={() => openEditEval(detail, ev)}
                                className="text-slate-400 hover:text-[#655dc6] p-1 transition-colors" title="تعديل التقييم"
                                data-testid={`eval-edit-${ev.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-600">بواسطة: <span className="font-medium">{ev.evaluated_by_name}</span> — درجة: {ev.condition_score ?? "—"}</p>
                        {ev.notes && <p className="text-sm text-slate-700">{ev.notes}</p>}
                        {ev.report_path && (
                          <button type="button" onClick={() => openEvalReport(detail.id, ev.id)}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-full px-2.5 py-1 font-semibold transition-colors"
                            data-testid={`eval-report-open-${ev.id}`}>
                            <FileText className="w-3 h-3" /> تقرير الوكيل PDF
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BiomedDevices;
